import React from 'react';
import { useMutation } from '@apollo/client/react';
import { MergeType } from '@mui/icons-material';
import { Button } from '@mui/material';
import ButtonDialog from './ButtonDialog';
import { useListContext } from 'components/list/ListContext';
import { useDialogContext } from 'components/dialog/DialogContext';
import QUERIES from 'services/gqlQueries';
import { toast } from 'react-toastify';
import { RecordType } from 'components/record/RecordContext';

export type DeviceRecord = RecordType & {
  name?: string;
  imei?: string;
  liveloxPersonId?: number | null;
  user?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    uid?: string;
  } | null;
};

const isLiveloxDevice = (device: DeviceRecord) => device.liveloxPersonId != null;

export const isUserUidDevice = (device: DeviceRecord) => device.user != null && device.imei === device.user.uid;

export const getMergeDevicesValidation = (selectedIds: Array<string | number>, list: DeviceRecord[]) => {
  if (selectedIds.length !== 2) {
    return 'Select exactly two devices to merge.';
  }

  const devices = list.filter((device) => selectedIds.includes(device.id));
  if (devices.length !== 2) {
    return 'Could not find both selected devices.';
  }

  const liveloxDevice = devices.find(isLiveloxDevice);
  const userDevice = devices.find(isUserUidDevice);

  if (!liveloxDevice || !userDevice) {
    return 'One device must have a Livelox person ID and the other must match a user uid.';
  }

  if (liveloxDevice.id === userDevice.id) {
    return 'Select two different devices.';
  }

  if (userDevice.liveloxPersonId != null) {
    return 'The user device already has a Livelox person ID.';
  }

  return null;
};

const BulkMergeDevicesConfirm = ({ onSuccess, validationError }: { onSuccess: () => void; validationError: string | null }) => {
  const { close } = useDialogContext();
  const { selected: ids } = useListContext();
  const [mutate, { loading }] = useMutation<{ item: DeviceRecord }>(QUERIES.mergeDevicesAllDevice);

  const handleConfirm = () => {
    if (validationError) {
      return;
    }

    mutate({ variables: { ids } })
      .then((res) => {
        if (res.data?.item) {
          toast.success(`Merged devices into ${res.data.item.name}.`);
          close();
          onSuccess();
        }
      })
      .catch((error: Error) => {
        if (error.message) {
          toast.error(error.message);
        }
      });
  };

  return (
    <Button variant="contained" onClick={handleConfirm} disabled={loading || !!validationError}>
      {loading ? 'Loading...' : 'Confirm'}
    </Button>
  );
};

const BulkMergeDevices = () => {
  const { refetch, setSelected, selected, list } = useListContext();
  const validationError = getMergeDevicesValidation(selected, list);

  const onSuccess = () => {
    setSelected([]);
    if (typeof refetch === 'function') {
      refetch();
    }
  };

  const selectedDevices = list.filter((device) => selected.includes(device.id));
  const liveloxDevice = selectedDevices.find(isLiveloxDevice);
  const userDevice = selectedDevices.find(isUserUidDevice);

  return (
    <ButtonDialog
      buttonLabel="Merge devices"
      variant="text"
      maxWidth="sm"
      startIcon={<MergeType />}
      dialogTitle="Merge devices"
      primaryButton={<BulkMergeDevicesConfirm onSuccess={onSuccess} validationError={validationError} />}
    >
      {validationError ? (
        validationError
      ) : (
        <>
          Merge the Livelox device <strong>{liveloxDevice?.name}</strong> into the user device <strong>{userDevice?.name}</strong>? The Livelox person
          ID will be kept on the user device and the Livelox-only device will be removed.
        </>
      )}
    </ButtonDialog>
  );
};

export default BulkMergeDevices;
